import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useQuery } from "react-query";
import { useRecoilValue } from "recoil";
import { Form, Formik, useField, useFormikContext } from "formik";
import * as yup from "yup";
import { DateTime } from "luxon";
import { Box, Checkbox, Paper, styled, Typography } from "@mui/material";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { DateRange } from "@mui/x-date-pickers-pro";

import {
  Dimension,
  DimensionValue,
  getDimension,
  listDimensions,
  listDimensionValues,
} from "../../services/Category";
import { granularityAtom } from "../AlertGranularityTabs";
import {
  PRICE_STATUS_FILTER_OPTIONS,
  UH_CAT_REFERENCE_FILTER_OPTIONS,
} from "../../Pricing/utils";
import { formInputsStyles } from "../../Configuration/styles";
import { CustomAutocomplete } from "../../components/Inputs/CustomAutocomplete";
import CustomDateRangePicker from "../../components/Inputs/DatePickers/CustomDateRangePicker";
import CustomTimeRangePicker from "../../components/Inputs/DatePickers/CustomTimeRangePicker";
import { FilterType } from "./FilterType";

interface FilterServicesContextType {
  listDimensions: (
    moduleSlug: string
  ) => Promise<{ count: number; results: any[] }>;
  listDimensionValues: (dimensionId: number) => Promise<any>;
  getDimension: (dimensionId: number) => Promise<any>;
  cacheNamespace: string;
  applyDimensionSpecialCases: boolean;
  // Some data sources (helicopter) return a translation key as the dimension
  // label; others return display text that must never go through intl.
  translateDimensionLabels: boolean;
  uhCatDimensionId: number;
}

const FilterServicesContext = createContext<FilterServicesContextType>({
  listDimensions,
  listDimensionValues,
  getDimension,
  cacheNamespace: "default",
  applyDimensionSpecialCases: true,
  translateDimensionLabels: false,
  uhCatDimensionId: 22,
});

export const FilterServicesProvider = FilterServicesContext.Provider;

export function useFilterServices() {
  return useContext(FilterServicesContext);
}

const LIMIT_TAG = 10;
const MAX_DISPLAYED_OPTIONS = 100;

export interface ComboBoxOption {
  label: string;
}

export type Value<T, Multiple> = Multiple extends undefined | false
  ? T | null
  : Array<T>;

export interface SelectValue {
  id: number;
  label: string;
  value: string;
}

type DimensionValueKeyType = keyof DimensionValue;

/**
 * Sort Query Data
 *
 * Sort data from API if there is multiple items
 * and if `order` key is present in the data.
 */
const sortQueryData = (queryData: DimensionValue[]) => {
  if (
    queryData.length > 1 &&
    queryData[0] &&
    typeof queryData[0].order !== "undefined"
  ) {
    queryData.sort((a, b) => {
      const arder = a.order ? a.order : -1;
      const brder = b.order ? b.order : -1;

      if (arder < brder) return -1;
      if (arder > brder) return 1;
      return 0;
    });
  }
  return queryData;
};

/**
 * Sort Query Data
 *
 * Sort lits by alphabetical order and with two level of grouping
 *
 * @param queryData
 * @param sortBy1
 * @param sortBy2
 */
const sortByAlphabeticMultipleFactor = (
  queryData: DimensionValue[],
  sortBy1: keyof DimensionValue,
  sortBy2: keyof DimensionValue
) => {
  const sortByParams1: DimensionValueKeyType[] = (sortBy1.split(
    "."
  ) as DimensionValueKeyType[]) || [sortBy1];
  const sortByParams2: DimensionValueKeyType[] = (sortBy2.split(
    "."
  ) as DimensionValueKeyType[]) || [sortBy2];

  return queryData.sort((a: any, b: any) => {
    if (
      sortByParams1[0] &&
      sortByParams1[1] &&
      sortByParams2[0] &&
      sortByParams2[1]
    ) {
      const valA1 = a[sortByParams1[0]]?.[sortByParams1[1]] ?? "";
      const valB1 = b[sortByParams1[0]]?.[sortByParams1[1]] ?? "";

      const primaryCompare = String(valA1).localeCompare(String(valB1));
      if (primaryCompare !== 0) return primaryCompare;

      const valA2 = a[sortByParams2[0]]?.[sortByParams2[1]] ?? "";
      const valB2 = b[sortByParams2[0]]?.[sortByParams2[1]] ?? "";

      return valA2.localeCompare(valB2);
    }

    return 0;
  });
};

interface GroupHeaderProps {
  isSubHeader?: boolean;
}

/**
 * Component use in autocomplete list in case of groupBy, it's the title group name
 */
const GroupHeader = styled("div")<GroupHeaderProps>(
  ({ theme, isSubHeader }) => ({
    boxSizing: "border-box",
    lineHeight: "24px",
    listStyle: "none",
    color: theme.vars.palette.text.secondary,
    fontFamily: "Lato, Helvetica Neue, Arial, Helvetica, sans-serif",
    fontWeight: 500,
    fontSize: "0.875rem",
    paddingLeft: isSubHeader ? theme.spacing(4) : theme.spacing(2),
    paddingRight: theme.spacing(2),
  })
);

/**
 * Component use in autocomplete list, it's common item
 */
const GroupItems = styled("div")(
  ({ isIndented }: { isIndented?: boolean }) => ({
    listStyle: "none",
    "& div": {
      marginLeft: isIndented ? "32px" : 0,
    },
  })
);

const ValueOption = <T extends ComboBoxOption>({
  option,
  selected,
}: {
  option: T;
  selected: boolean;
}) => {
  return (
    <div
      style={{
        alignItems: "center",
        display: "flex",
        height: "15px",
        width: "100%",
      }}
    >
      <Checkbox
        checked={selected}
        checkedIcon={checkedIcon}
        size="small"
        sx={{ mr: 1, height: "10px", width: "10px" }}
      />
      <p
        style={{
          display: "block",
          fontSize: "0.8rem",
          margin: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          width: "100%",
        }}
      >
        {option.label}
      </p>
    </div>
  );
};

export const checkedIcon = <CheckBoxIcon fontSize="small" color={"primary"} />;

export const CustomPaper = ({
  children,
  ...paperProps
}: {
  children?: any;
}) => {
  return (
    <Paper
      {...paperProps}
      sx={{ "& .MuiAutocomplete-listbox": { maxHeight: "28vh" } }}
    >
      <Typography
        component="p"
        sx={{ fontSize: 12, m: 0, py: 0.625, px: 1.25, color: "primary.main" }}
      >
        <FormattedMessage id="AUTOCOMPLETE_WARNING" />
      </Typography>
      {children}
    </Paper>
  );
};

export const DimensionValueList = () => {
  const inputStyles = formInputsStyles();
  const autocompleteRef = useRef<HTMLDivElement>(null);
  const [field, meta, helpers] = useField("values");
  const intl = useIntl();
  const {
    isSubmitting,
    setFieldValue,
    touched,
    values: { dimension },
  } = useFormikContext<DimensionFormValues>();
  const {
    listDimensionValues: fetchDimensionValues,
    cacheNamespace,
    applyDimensionSpecialCases,
    uhCatDimensionId,
  } = useContext(FilterServicesContext);
  let previousGroupName = "";

  // Will not be called with undefined value because of `enabled`
  const query = useQuery(
    [cacheNamespace, "dimensions", dimension?.id],
    () => fetchDimensionValues(dimension?.id as number),
    {
      enabled: !!dimension,
      select: (response): DimensionValue[] => {
        let queryData = response.results;

        if (applyDimensionSpecialCases) {
          // When weekday filter is selected translate days name (id dimension 29 = "Jour de semaine")
          if (dimension?.id === 29) {
            queryData = queryData.map((day: any) => {
              return {
                ...day,
                label: intl.formatMessage({ id: day.name.toUpperCase() }),
              };
            });
          }

          // When price status filter is selected hardcode and translate options
          if (dimension?.id === 35) {
            queryData = PRICE_STATUS_FILTER_OPTIONS.map((option) => {
              return {
                ...option,
                label: intl.formatMessage({ id: option.label }),
              };
            });
          }

          // When uh cat reference filters are selected, hardcode and translate
          // boolean options (id 41 = property reference, id 42 = uh type reference)
          if (dimension?.id === 41 || dimension?.id === 42) {
            queryData = UH_CAT_REFERENCE_FILTER_OPTIONS.map((option) => {
              return {
                ...option,
                label: intl.formatMessage({ id: option.label }),
              };
            });
          }

          // When uhcat filter is selected sort list by property and by uhtype to group them
          if (dimension?.id === uhCatDimensionId) {
            return sortByAlphabeticMultipleFactor(
              queryData.filter((el: any) => el.uh_type_nandc?.short_name),
              "property.hebergement_nom_court" as keyof DimensionValue,
              "uh_type_nandc.short_name" as keyof DimensionValue
            );
          }
        }

        return sortQueryData(queryData);
      },
    }
  );

  const selectedValues: DimensionValue[] = useMemo(
    () => field.value ?? [],
    [field.value]
  );

  const handleChange = (
    event: React.SyntheticEvent,
    selectedOptions: DimensionValue | DimensionValue[] | null
  ) => {
    // `multiple` always gives an array, but the type is widened by the `field` spread
    helpers.setValue(Array.isArray(selectedOptions) ? selectedOptions : []);
  };

  useEffect(() => {
    if (touched.dimension && !isSubmitting) {
      setFieldValue("values", []);
    }
  }, [dimension, isSubmitting, setFieldValue, touched.dimension]);

  return (
    <Box
      className={`${inputStyles.classes.container} ${inputStyles.classes.inputSize}`}
    >
      <CustomAutocomplete
        multiple
        disableCloseOnSelect
        limitTags={LIMIT_TAG}
        {...field}
        onChange={handleChange}
        maxDisplayedOptions={MAX_DISPLAYED_OPTIONS}
        fullWidth
        isOptionEqualToValue={(option: DimensionValue, value: DimensionValue) =>
          option.id === value.id
        }
        getOptionLabel={(option: DimensionValue) => option.label}
        groupBy={(option) => {
          if (
            applyDimensionSpecialCases &&
            dimension?.id === uhCatDimensionId &&
            option.property &&
            option.uh_type_nandc
          ) {
            return (
              option.property.hebergement_nom_court +
              "__" +
              option.uh_type_nandc.short_name
            );
          }

          return option.label;
        }}
        renderGroup={(params) => {
          if (
            applyDimensionSpecialCases &&
            dimension?.id === uhCatDimensionId
          ) {
            const hierarchy = params.group.split("__");
            if (hierarchy[0]) {
              const showGroupHeader = hierarchy[0] !== previousGroupName;
              previousGroupName = hierarchy[0];
              return (
                <div key={params.key}>
                  {showGroupHeader && <GroupHeader>{hierarchy[0]}</GroupHeader>}
                  <GroupHeader isSubHeader>{hierarchy[1]}</GroupHeader>
                  <GroupItems isIndented>{params.children}</GroupItems>
                </div>
              );
            }
          }

          return (
            <div key={params.key}>
              <GroupItems>{params.children}</GroupItems>
            </div>
          );
        }}
        options={query.data ?? []}
        loading={query.isLoading}
        rootRef={autocompleteRef}
        error={!!(meta.touched && meta.error)}
        helperText={(meta.touched && meta.error) || ""}
        label={intl.formatMessage({ id: "CHOICE" })}
        renderOption={(props, option, { selected }) => (
          <li {...props} key={option.id}>
            <ValueOption option={option} selected={selected} />
          </li>
        )}
        renderSelectAllOption={({ key, ...liProps }, { label, selected }) => (
          <li key={key} {...liProps}>
            <ValueOption option={{ label }} selected={selected} />
          </li>
        )}
        value={selectedValues}
        slots={{
          paper: CustomPaper,
        }}
        slotProps={{
          popper: {
            style: {
              width: autocompleteRef?.current?.clientWidth,
            },
          },
        }}
      />
    </Box>
  );
};

const DimensionDateAndTimeRangePicker = () => {
  const inputStyles = formInputsStyles();
  const [dateValue, setDateValue] = useState<DateRange<DateTime<boolean>>>([
    null,
    null,
  ]);
  const [timeValue, setTimeValue] = useState<DateRange<DateTime<boolean>>>([
    null,
    null,
  ]);
  const [field, , helpers] = useField("values");

  const handleChange = (value: DateRange<any>, type: "date" | "time") => {
    let fieldValue = field.value;
    let startValues: string[] = [];
    let endValues: string[] = [];

    if (fieldValue[0]) {
      startValues = fieldValue[0].split(" ");
    }
    if (fieldValue[1]) {
      endValues = fieldValue[1].split(" ");
    }

    if (type === "date") {
      if (value[0]?.isValid) {
        startValues[0] = value[0].toFormat("yyyy-MM-dd");
      }

      if (value[1]?.isValid) {
        endValues[0] = value[1].toFormat("yyyy-MM-dd");
      }
    } else if (type === "time") {
      if (value[0]?.isValid) {
        if (startValues[0]) {
          startValues[1] = value[0].toFormat("HH:mm");
        } else {
          startValues[0] = DateTime.now().toFormat("yyyy-MM-dd");
          startValues[1] = value[0].toFormat("HH:mm");
        }
      }

      if (value[1]?.isValid) {
        if (endValues[0]) {
          endValues[1] = value[1].toFormat("HH:mm");
        } else {
          endValues[0] = DateTime.now().toFormat("yyyy-MM-dd");
          endValues[1] = value[1].toFormat("HH:mm");
        }
      }
    }

    let newStartDate = null;
    let newEndDate = null;

    // We remove one hour to be ok with date timezone od backend data
    // This will be to remove when backend will be fix
    if (startValues[1]) {
      let ISODate = startValues.join("T");
      newStartDate = DateTime.fromISO(ISODate)
        .minus({ hours: 1 })
        .toFormat("yyyy-MM-dd HH:mm");
    }
    if (endValues[1]) {
      let ISODate = endValues.join("T");
      newEndDate = DateTime.fromISO(ISODate)
        .minus({ hours: 1 })
        .toFormat("yyyy-MM-dd HH:mm");
    }

    let newValues = [
      startValues[1] ? newStartDate : startValues[0],
      endValues[1] ? newEndDate : endValues[0],
    ];

    helpers.setValue(newValues);
  };

  // We add one hour to be ok with date timezone od backend data
  // This will be to remove when backend will be fix
  useEffect(() => {
    let date = [];
    let hour = [];

    if (field.value[0]) {
      let splitedDate = field.value[0].split(" ");

      if (splitedDate[1]) {
        let ISODate = splitedDate.join("T");
        splitedDate = DateTime.fromISO(ISODate)
          .plus({ hours: 1 })
          .toFormat("yyyy-MM-dd HH:mm")
          .split(" ");
      }

      date[0] = DateTime.fromISO(splitedDate[0]);

      if (splitedDate[1]) {
        const dt = DateTime.fromFormat(splitedDate[1], "HH:mm").set({
          year: DateTime.now().year,
          month: DateTime.now().month,
          day: DateTime.now().day,
        });

        hour[0] = dt;
      } else {
        hour[0] = null;
      }
    } else {
      date[0] = null;
      hour[0] = null;
    }

    if (field.value[1]) {
      let splitedDate = field.value[1].split(" ");

      if (splitedDate[1]) {
        let ISODate = splitedDate.join("T");
        splitedDate = DateTime.fromISO(ISODate)
          .plus({ hours: 1 })
          .toFormat("yyyy-MM-dd HH:mm")
          .split(" ");
      }

      date[1] = DateTime.fromISO(splitedDate[0]);

      if (splitedDate[1]) {
        const dt = DateTime.fromFormat(splitedDate[1], "HH:mm").set({
          year: DateTime.now().year,
          month: DateTime.now().month,
          day: DateTime.now().day,
        });

        hour[1] = dt;
      } else {
        hour[1] = null;
      }
    } else {
      date[1] = null;
      hour[1] = null;
    }

    setDateValue(date as DateRange<DateTime<boolean>>);
    setTimeValue(hour as DateRange<DateTime<boolean>>);
  }, [field.value]);

  return (
    <Box
      className={`${inputStyles.classes.container} ${inputStyles.classes.inputSize}`}
      sx={{
        display: "flex",
        flexDirection: "column",
        pt: 2,
        gap: 2,
      }}
    >
      <CustomDateRangePicker
        {...field}
        disablePast={false}
        fieldSeparator={<FormattedMessage id="TO" />}
        onChange={handleChange}
        value={dateValue}
      />
      <CustomTimeRangePicker
        {...field}
        onChange={handleChange}
        value={timeValue}
      />
    </Box>
  );
};

export const DimensionValueForm = () => {
  const {
    values: { dimension },
  } = useFormikContext<DimensionFormValues>();

  return (
    <>
      {dimension?.label === "Modifié le" ? (
        <DimensionDateAndTimeRangePicker />
      ) : (
        <DimensionValueList />
      )}
    </>
  );
};

export interface FilterDimensionForm {
  target?: number;
  values: DimensionValue[] | string[];
  type?: FilterType;
}

/**
 * Type of the form
 *
 * dimension must be nullable because we do not have the list of values initially
 */
export interface DimensionFormValues {
  dimension: Dimension | null;
  values: DimensionValue[] | string[];
  type?: FilterType;
}

export const DimensionFormField = ({
  dimensionId,
  moduleSlug,
}: {
  dimensionId?: number;
  moduleSlug: string;
}) => {
  const inputStyles = formInputsStyles();
  const {
    listDimensions: fetchDimensions,
    cacheNamespace: ns,
    translateDimensionLabels,
  } = useContext(FilterServicesContext);
  const query = useQuery([ns, "dimensions", moduleSlug], () =>
    fetchDimensions(moduleSlug)
  );
  const {
    setFieldTouched,
    setFieldValue,
    values: { dimension },
  } = useFormikContext<DimensionFormValues>();
  const [field, meta, helpers] = useField("dimension");
  const intl = useIntl();

  useEffect(() => {
    if (query.data && !dimension) {
      const dimensions = query.data?.results || [];
      const dimension =
        dimensions.find((dim) => dim.id === dimensionId) ||
        dimensions[0] ||
        null;
      setFieldValue("dimension", dimension);
    }
  }, [dimension, dimensionId, query.data, setFieldValue]);

  const granularityValue = useRecoilValue(granularityAtom);

  return (
    <Box
      className={`${inputStyles.classes.container} ${inputStyles.classes.inputSize}`}
    >
      <CustomAutocomplete
        {...field}
        onChange={(_, value) => {
          setFieldTouched(field.name);
          helpers.setValue(value);
        }}
        fullWidth
        isOptionEqualToValue={(option: Dimension, value: Dimension) =>
          option?.id === value.id
        }
        getOptionLabel={(option: Dimension) =>
          translateDimensionLabels
            ? intl.formatMessage({ id: option.label })
            : option.label
        }
        loading={query.isLoading}
        options={query.data?.results || []}
        getOptionDisabled={(option) =>
          moduleSlug === "cockpit"
            ? false
            : option.granularity > granularityValue
        }
        error={!!(meta.touched && meta.error)}
        helperText={(meta.touched && meta.error) || ""}
        label={intl.formatMessage({ id: "DIMENSION" })}
        size="small"
      />
    </Box>
  );
};

export const CreateFilterForm = ({
  model,
  onSubmit,
  moduleSlug,
}: {
  model: FilterDimensionForm;
  onSubmit: (model: Required<FilterDimensionForm>) => void;
  moduleSlug: string;
}) => {
  const inputStyles = formInputsStyles();
  // Putting the schema in the component because it needs
  // the locale to be set first
  const [schema] = useState(() =>
    yup
      .object({
        dimension: yup.object().required(),
        values: yup.array().min(1).required(),
      })
      .required()
  );

  const initialValues: DimensionFormValues = {
    dimension: null,
    values: model.values,
  };

  // dimension can not be null because it is required
  // TODO: find better way to assert the type with Formik
  const handleSubmitForm = (data: DimensionFormValues) => {
    onSubmit({
      target: data.dimension?.id as number,
      values: data.values,
      type: data.dimension?.type || FilterType.MULTIPLE,
    });
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmitForm}
      validationSchema={schema}
    >
      <Form id="create-filter-form" className={inputStyles.classes.root}>
        <DimensionFormField
          dimensionId={model.target}
          moduleSlug={moduleSlug}
        />
        <DimensionValueForm />
      </Form>
    </Formik>
  );
};
